/**
 * The path a reader walked from map to map, so "Explore from here" has a way
 * back. It lives in session storage: it is this tab's walk, not a fact, and
 * it is not worth a URL. Storage that is blocked or corrupt leaves the page
 * working with no trail.
 */
export interface Crumb {
  readonly ein: string;
  readonly name: string | null;
}

export const TRAIL_LIMIT = 8;
const KEY = 'fundergraph:trail';

const storageOf = (storage: Storage | null | undefined): Storage | null => {
  if (storage !== undefined) {
    return storage;
  }
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
};

const isCrumb = (value: unknown): value is Crumb =>
  typeof value === 'object' && value !== null && typeof (value as { ein?: unknown }).ein === 'string' && ((value as { name?: unknown }).name === null || typeof (value as { name?: unknown }).name === 'string');

export const readTrail = (storage?: Storage | null): readonly Crumb[] => {
  const store = storageOf(storage);
  if (store === null) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(store.getItem(KEY) ?? '[]');
    return Array.isArray(parsed) && parsed.every(isCrumb) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (crumbs: readonly Crumb[], storage?: Storage | null): readonly Crumb[] => {
  const store = storageOf(storage);
  if (store === null) {
    return [];
  }
  try {
    store.setItem(KEY, JSON.stringify(crumbs));
    return crumbs;
  } catch {
    return [];
  }
};

export const pushCrumb = (crumb: Crumb, storage?: Storage | null): readonly Crumb[] =>
  write([...readTrail(storage).filter((existing) => existing.ein !== crumb.ein), crumb].slice(-TRAIL_LIMIT), storage);

/** The crumbs before the one a reader returned to; that anchor's own page becomes the current one. */
export const truncateTrail = (ein: string, storage?: Storage | null): readonly Crumb[] => {
  const trail = readTrail(storage);
  const index = trail.findIndex((crumb) => crumb.ein === ein);
  return write(index < 0 ? trail : trail.slice(0, index), storage);
};

export const clearTrail = (storage?: Storage | null): void => {
  const store = storageOf(storage);
  if (store === null) {
    return;
  }
  try {
    store.removeItem(KEY);
  } catch {
    // Blocked storage: nothing to clear.
  }
};
